/**
 * Image Overlay — vizz.fm parity feature.
 *
 * Renders a user-uploaded image as a DOM <img> element layered over the
 * WebGL canvas. Supports CSS blend modes, opacity, scale, and X/Y position.
 * Persisted via localStorage as a data-URL so refresh keeps the overlay.
 */

export interface ImageOverlayConfig {
  visible: boolean;
  /** Data URL of the uploaded image, or null. */
  src: string | null;
  opacity: number; // 0..1
  blendMode: GlobalCompositeOperation | "normal";
  scale: number; // 0.1..3
  posX: number; // 0..1 fraction of viewport width
  posY: number; // 0..1
}

export const DEFAULT_OVERLAY: ImageOverlayConfig = {
  visible: false,
  src: null,
  opacity: 1,
  blendMode: "normal",
  scale: 1,
  posX: 0.5,
  posY: 0.5,
};

const KEY = "pulsefx.imageOverlay.v1";

export class ImageOverlay {
  private el: HTMLImageElement;
  public cfg: ImageOverlayConfig = { ...DEFAULT_OVERLAY };

  constructor(parent: HTMLElement) {
    this.el = document.createElement("img");
    this.el.style.position = "fixed";
    this.el.style.pointerEvents = "none";
    this.el.style.zIndex = "5"; // above #frame, below HUD
    this.el.style.transformOrigin = "center";
    this.el.style.transition = "transform 80ms linear, opacity 80ms linear";
    this.el.style.maxWidth = "none";
    this.el.style.maxHeight = "none";
    this.el.style.display = "none";
    parent.appendChild(this.el);
    this.load();
    this.apply();
  }

  load(): void {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) Object.assign(this.cfg, JSON.parse(raw));
    } catch {
      /* ignore */
    }
  }

  save(): void {
    try {
      localStorage.setItem(KEY, JSON.stringify(this.cfg));
    } catch {
      /* ignore */
    }
  }

  setSrc(src: string | null): void {
    this.cfg.src = src;
    this.save();
    this.apply();
  }

  update(partial: Partial<ImageOverlayConfig>): void {
    Object.assign(this.cfg, partial);
    this.save();
    this.apply();
  }

  /**
   * Snapshot of overlay state used by RecordingCompositor to bake this
   * overlay into recordings + snapshots (since `canvas.captureStream()`
   * doesn't see DOM elements layered over the WebGL canvas).
   */
  getRenderState(): ImageOverlayConfig {
    return { ...this.cfg };
  }

  apply(): void {
    if (!this.cfg.visible || !this.cfg.src) {
      this.el.style.display = "none";
      return;
    }
    this.el.src = this.cfg.src;
    this.el.style.display = "block";
    this.el.style.opacity = String(this.cfg.opacity);
    this.el.style.mixBlendMode = this.cfg.blendMode;
    // Position relative to viewport.
    const x = this.cfg.posX * 100;
    const y = this.cfg.posY * 100;
    this.el.style.left = `${x}%`;
    this.el.style.top = `${y}%`;
    this.el.style.transform = `translate(-50%, -50%) scale(${this.cfg.scale})`;
  }
}
